import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type {
  ClaireRegressionJudge,
  ClaireRegressionMetric,
  ClaireRegressionRun,
  ClaireRegressionRunsReport,
} from '@borradh-workspace/api-client/types';
import { ExternalLink } from 'lucide-react';
import { OUTCOMES, type OutcomeKey, formatPct } from './live-quality-format';
import { formatRunDate } from './regression-format';
import type { ExplorerRequest } from './turn-explorer';

type Open = (r: ExplorerRequest) => void;

const METRIC: Record<OutcomeKey, keyof ClaireRegressionRun['headline']> = {
  handled: 'resolvedShare',
  handedOff: 'handedOffShare',
  failing: 'stalledOrFailedShare',
};

/**
 * The judges in plain words. `v2` is the live judge (one Gemini call per
 * reply, the one production runs on); `langfuse` the older evaluation rules.
 */
export const JUDGE_LABELS: Record<ClaireRegressionJudge, string> = {
  v2: 'New judge',
  langfuse: 'Old judges',
};

/** Where a run's before numbers come from. */
export function beforeText(run: ClaireRegressionRun): string {
  if (run.judge !== 'v2') return 'Before is the reply that shipped.';
  return run.failureTypes.baseline
    ? `Before is main's replay, ${run.failureTypes.baseline.name}.`
    : 'No earlier run the new judge scored to compare with.';
}

/** Which way is good for each outcome: up, down, or neither. */
const GOOD: Record<OutcomeKey, 'up' | 'down'> = {
  handled: 'up',
  handedOff: 'down',
  failing: 'down',
};

const PLAIN_CHECKS: Record<string, string> = {
  frustration: 'customers got more frustrated',
  'frustration items': 'frustrated customers got more frustrated',
  'high-frustration items': 'very frustrated customers got more frustrated',
  'stalled share': 'more conversations stalled',
  'failed share': 'more conversations failed',
  'resolved share': 'fewer conversations handled by Claire',
  'items without a frustration tag':
    'conversations that were fine got more frustrating',
};

/** The run's verdict in one sentence. */
export function runVerdict(run: ClaireRegressionRun): string {
  if (run.status === 'scoring') return 'Still being judged.';
  if (run.status === 'incomplete')
    return 'The judges did not finish this run, so it has no verdict.';
  if (run.status === 'no-baseline')
    return 'First run the new judge scored: nothing to compare with yet.';
  if (run.status === 'pass')
    return 'Passed: nothing got worse beyond the limits.';
  const why = run.regressions.map((r) => {
    const [label, detail] = r.split(/:\s(.*)/s);
    const plain = PLAIN_CHECKS[label ?? ''] ?? label;
    return /→/.test(detail ?? '') ? `${plain} (${detail})` : plain;
  });
  return `Failed: ${why.join('; ')}.`;
}

function arrowClass(m: ClaireRegressionMetric, good: 'up' | 'down') {
  if (m.delta === null || Math.abs(m.delta) < 0.02)
    return 'text-muted-foreground';
  return m.delta > 0 === (good === 'up')
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-600 dark:text-red-400';
}

const STATUS_BADGE: Record<ClaireRegressionRun['status'], string> = {
  pass: 'Pass',
  fail: 'Fail',
  scoring: 'Judging',
  incomplete: 'Incomplete',
  'no-baseline': 'No baseline',
};

/** The other judge's before → after under a number, in smaller type. */
function OtherJudgeLine({
  judge,
  before,
  after,
}: {
  judge: ClaireRegressionJudge;
  before: string;
  after: string;
}) {
  return (
    <p className="text-muted-foreground mt-1 text-xs tabular-nums">
      {JUDGE_LABELS[judge]}: {before} → {after}
    </p>
  );
}

function RunCard({ run, onOpen }: { run: ClaireRegressionRun; onOpen: Open }) {
  const open = (
    title: string,
    extra: {
      outcome?: 'handled' | 'handed_off' | 'failing';
      side?: 'before' | 'after';
      worse?: boolean;
      mistake?: 'serious';
    }
  ) =>
    onOpen({
      title: `${title} · ${run.name}`,
      filter: {
        source: 'run',
        runId: run.id,
        datasetName: run.datasetName,
        // The explorer reads the same judge as the card; under the new
        // judge its before side is the baseline run.
        ...(run.judge === 'v2'
          ? {
              judge: 'v2' as const,
              baselineRunId: run.failureTypes.baseline?.id,
            }
          : {}),
        ...extra,
      },
    });
  const judged = run.status === 'pass' || run.status === 'fail';
  const other = run.otherJudge;
  return (
    <Card data-testid={`run-${run.name}`}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {run.name}
          <Badge
            variant={
              run.status === 'fail'
                ? 'destructive'
                : run.status === 'pass'
                  ? 'secondary'
                  : 'outline'
            }
          >
            {STATUS_BADGE[run.status]}
          </Badge>
          <Badge variant="outline" data-testid="run-judge">
            {JUDGE_LABELS[run.judge]}
          </Badge>
        </CardTitle>
        <CardDescription>
          {formatRunDate(run.startedAt)} · {run.items} conversations ·{' '}
          {beforeText(run)} {runVerdict(run)}
        </CardDescription>
        <CardAction>
          <a
            href={run.url}
            target="_blank"
            rel="noreferrer"
            className="text-primary inline-flex items-center gap-1 text-xs hover:underline"
          >
            Langfuse <ExternalLink className="size-3" />
          </a>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-4">
          {OUTCOMES.map((o) => {
            const m = run.headline[METRIC[o.key]];
            return (
              <div key={o.key} className="rounded-lg border p-3">
                <div className="text-muted-foreground text-xs">{o.label}</div>
                <div className="flex items-baseline gap-2 text-lg font-semibold tabular-nums">
                  <button
                    type="button"
                    className="text-muted-foreground hover:underline"
                    onClick={() =>
                      open(`${o.label}, before`, {
                        outcome: o.filter,
                        side: 'before',
                      })
                    }
                  >
                    {formatPct(m.before)}
                  </button>
                  <span className="text-muted-foreground text-sm">→</span>
                  <button
                    type="button"
                    className={cn(
                      'hover:underline',
                      arrowClass(m, GOOD[o.key])
                    )}
                    onClick={() =>
                      open(`${o.label}, after`, {
                        outcome: o.filter,
                        side: 'after',
                      })
                    }
                  >
                    {formatPct(m.after)}
                  </button>
                </div>
                {other ? (
                  <OtherJudgeLine
                    judge={other.judge}
                    before={formatPct(other.headline[METRIC[o.key]].before)}
                    after={formatPct(other.headline[METRIC[o.key]].after)}
                  />
                ) : null}
                {o.key === 'handedOff' &&
                run.handoffCounting === 'before-fix' ? (
                  <p className="text-muted-foreground mt-1 text-xs">
                    Too high after: run predates the handover-count fix (#1936)
                    and counts later staff takeovers.
                  </p>
                ) : null}
              </div>
            );
          })}
          <div className="rounded-lg border p-3">
            <div className="text-muted-foreground text-xs">
              Serious mistakes
            </div>
            {run.serious ? (
              <div className="flex items-baseline gap-2 text-lg font-semibold tabular-nums">
                <span className="text-muted-foreground">
                  {run.serious.before}
                </span>
                <span className="text-muted-foreground text-sm">→</span>
                <button
                  type="button"
                  className={cn(
                    'hover:underline',
                    run.serious.after > run.serious.before
                      ? 'text-red-600 dark:text-red-400'
                      : run.serious.after < run.serious.before
                        ? 'text-green-600 dark:text-green-400'
                        : ''
                  )}
                  onClick={() =>
                    open('Serious mistakes, after', { mistake: 'serious' })
                  }
                >
                  {run.serious.after}
                </button>
                <span className="text-muted-foreground text-xs font-normal">
                  of {run.serious.items}
                </span>
              </div>
            ) : (
              <div className="text-muted-foreground text-sm">Not judged</div>
            )}
            {other?.serious ? (
              <OtherJudgeLine
                judge={other.judge}
                before={String(other.serious.before)}
                after={`${other.serious.after} of ${other.serious.items}`}
              />
            ) : null}
          </div>
        </div>
        {judged ? (
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => open('Got worse in this run', { worse: true })}
            >
              See conversations that got worse
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function RunList({
  title,
  description,
  runs,
  onOpen,
}: {
  title: string;
  description: string;
  runs: readonly ClaireRegressionRun[];
  onOpen: Open;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
      {runs.length === 0 ? (
        <p className="text-muted-foreground text-sm">No runs yet.</p>
      ) : (
        runs.map((run) => (
          <RunCard
            key={`${run.datasetId}:${run.id}`}
            run={run}
            onOpen={onOpen}
          />
        ))
      )}
    </div>
  );
}

/** Each test run: the same three numbers before and after, and a verdict. */
export function TestRunsSection({
  report,
  onOpen,
}: {
  report: ClaireRegressionRunsReport;
  onOpen: Open;
}) {
  if (!report.configured)
    return (
      <Alert>
        <AlertTitle>Langfuse is not connected on this API</AlertTitle>
        <AlertDescription>
          Set CLAIRE_REGRESSION_LANGFUSE_PUBLIC_KEY and
          CLAIRE_REGRESSION_LANGFUSE_SECRET_KEY on the API.
        </AlertDescription>
      </Alert>
    );
  const problem = report.runs.filter((r) => r.set === 'problem');
  const good = report.runs.filter((r) => r.set === 'good');
  return (
    <div className="flex flex-col gap-8">
      <RunList
        title="Did we fix it?"
        description="Past conversations that went badly, replayed through the changed Claire. After is the new reply; each card says what before is."
        runs={problem}
        onOpen={onOpen}
      />
      {good.length ? (
        <RunList
          title="Did we break it?"
          description="Past conversations that went well, replayed the same way. These should stay the same."
          runs={good}
          onOpen={onOpen}
        />
      ) : null}
    </div>
  );
}
